import { lazy } from 'react';

import { ComputeCenterOverview } from '@/components-biz/ComputeCenterOverview';
import i18n from '@/i18n';
import AppLayout from '@/layout/AppLayout';
import LeftNavLayout from '@/layout/LeftNavLayout';
import { lazyNamedImport } from '@/utils/lazyNamedImport';

import ErrorBoundary from '../ErrorBoundary';
import { joinPrefix, mergeRouters } from '../util';
import {
  aiCenterDc,
  alertCenter,
  apiKeyManagement,
  appCenter,
  appCenterDcOnly,
  artifactCenter,
  auditLog,
  clusterAsset,
  costCenter as commonCostCenter,
  instanceManagement,
  intelligentStorage,
  logCenter,
  monitorCenter,
  networkSecurity,
  operationOverview,
} from './dcSharedWs';
import { SUB_CENTER, createSubCenterRoutes } from './subCenterRoutes';

const t = i18n.t.bind(i18n);

const BareMetalGateway = lazy(() => import('@/pages/data-center/assets/network'));
const DataCenterOverview = lazy(() => import('@/pages/data-center/overview'));
const BareMetalGatewayDetail = lazy(() => import('@/pages/data-center/assets/network/detail'));
const BareMetalGatewayEdit = lazy(() => import('@/pages/data-center/assets/network/edit'));
const BareMetalNode = lazy(() => import('@/pages/data-center/assets/computing'));
const BareMetalNodeEdit = lazy(() => import('@/pages/data-center/assets/computing/add'));
const BareMetalNodeDetail = lazy(() => import('@/pages/data-center/assets/computing/detail'));
const BareMetalPool = lazy(() => import('@/pages/data-center/assets/bare-metal-pool'));
const BareMetalPoolDetail = lazy(() => import('@/pages/data-center/assets/bare-metal-pool/detail'));
const BareMetalNodeGroupCreate = lazy(() => import('@/pages/data-center/assets/bare-metal-pool/create'));
const ElectricReport = lazyNamedImport(() => import('@/pages/data-center/situation-awareness/elec-report'), 'ElecReport');
const Node = lazyNamedImport(() => import('@/pages/data-center/computing-resources/nodes'), 'Node');
const NodeDetails = lazyNamedImport(() => import('@/pages/data-center/computing-resources/nodes/detail'), 'NodeDetails');
const EditLabel = lazyNamedImport(() => import('@/pages/data-center/computing-resources/nodes/detail/editLabel'), 'EditLabel');
const Roles = lazy(() => import('@/pages/data-center/security-center/roles'));
const Members = lazy(() => import('@/pages/data-center/security-center/members'));
const StorageClass = lazyNamedImport(() => import('@/pages/data-center/assets/storage'), 'StorageClass');
const CreateStorageClass = lazyNamedImport(() => import('@/pages/data-center/assets/storage/components/Create'), 'CreateStorageClass');
const StorageClassDetail = lazyNamedImport(() => import('@/pages/data-center/assets/storage/Detail'), 'StorageClassDetail');
const ProductSku = lazyNamedImport(() => import('@/pages/data-center/operation-center/product-skus'), 'ProductSku');
const ProductSkuCreate = lazyNamedImport(() => import('@/pages/data-center/operation-center/product-skus/create'), 'ProductSkuCreate');
const ProductSkuDetail = lazyNamedImport(() => import('@/pages/data-center/operation-center/product-skus/detail'), 'ProductSkuDetail');
const BillItems = lazyNamedImport(() => import('@/pages/data-center/operation-center/bill-items'), 'BillItems');
const BillItemsCreate = lazyNamedImport(() => import('@/pages/data-center/operation-center/bill-items/create'), 'BillItemsCreate');
const BillItemsDetail = lazyNamedImport(() => import('@/pages/data-center/operation-center/bill-items/detail'), 'BillItemsDetail');
const BillModels = lazyNamedImport(() => import('@/pages/data-center/operation-center/bill-models'), 'BillModels');
const BillModelsCreate = lazyNamedImport(() => import('@/pages/data-center/operation-center/bill-models/create'), 'BillModelsCreate');
const BillModelsDetail = lazyNamedImport(() => import('@/pages/data-center/operation-center/bill-models/detail'), 'BillModelsDetail');
const BindBillItemPage = lazyNamedImport(() => import('@/pages/data-center/operation-center/bill-models/detail/bind'), 'BindBillItemPage');
const AtomResources = lazyNamedImport(() => import('@/pages/data-center/operation-center/atom-resources'), 'AtomResources');
const DeployTypes = lazyNamedImport(() => import('@/pages/data-center/operation-center/deploy-types'), 'DeployTypes');
const Tags = lazyNamedImport(() => import('@/pages/data-center/operation-center/tags'), 'Tags');
const TagCreatePage = lazyNamedImport(() => import('@/pages/data-center/operation-center/tags/create'), 'TagCreatePage');
const TagDetail = lazyNamedImport(() => import('@/pages/data-center/operation-center/tags/detail'), 'TagDetail');
const CouponStrategyPage = lazyNamedImport(() => import('@/pages/data-center/operation-center/coupon-strategy'), 'CouponStrategyPage');
const CouponStrategyCreatePage = lazyNamedImport(
  () => import('@/pages/data-center/operation-center/coupon-strategy/create'),
  'CouponStrategyCreatePage',
);
const CouponStrategyDetailPage = lazyNamedImport(
  () => import('@/pages/data-center/operation-center/coupon-strategy/detail'),
  'CouponStrategyDetailPage',
);
const SAContainer = lazyNamedImport(() => import('@/pages/data-center/situation-awareness/container/sa-container'), 'SAContainer');
const SAContainerDetail = lazyNamedImport(
  () => import('@/pages/data-center/situation-awareness/container/detail/sa-container-detail'),
  'SAContainerDetail',
);
const SAGpu = lazyNamedImport(() => import('@/pages/data-center/situation-awareness/gpu/sa-gpu'), 'SAGpu');
const SAGpuDetail = lazyNamedImport(() => import('@/pages/data-center/situation-awareness/gpu/detail/sa-gpu-detail'), 'SAGpuDetail');
const SACluster = lazyNamedImport(() => import('@/pages/data-center/situation-awareness/cluster/sa-cluster'), 'SACluster');
const SAClusterDetail = lazyNamedImport(
  () => import('@/pages/data-center/situation-awareness/cluster/detail/sa-cluster-detail'),
  'SAClusterDetail',
);
const SAResource = lazyNamedImport(() => import('@/pages/data-center/situation-awareness/setting/sa-resource'), 'SAResource');
const SAResourceAdd = lazyNamedImport(() => import('@/pages/data-center/situation-awareness/setting/sa-resource-add'), 'SAResourceAdd');
const SAResourceDetail = lazyNamedImport(
  () => import('@/pages/data-center/situation-awareness/setting/detail/sa-resource-detail'),
  'SAResourceDetail',
);
const Charge = lazyNamedImport(() => import('@/pages/data-center/cost-center/income-detail'), 'Charge');
const NetworkDevice = lazy(() => import('@/pages/data-center/assets/network-device'));
const NetworkDeviceDetail = lazy(() => import('@/pages/data-center/assets/network-device/detail'));
const NetworkDeviceCreate = lazy(() => import('@/pages/data-center/assets/network-device/create'));
const Hardware = lazy(() => import('@/pages/data-center/assets/hardware'));
const HardwareDetail = lazy(() => import('@/pages/data-center/assets/hardware/detail'));
const HardwareInstanceDetail = lazy(() => import('@/pages/data-center/assets/hardware/instance/detail'));
const HardwareEdit = lazy(() => import('@/pages/data-center/assets/hardware/edit'));
// 概览
const overview = [
  {
    path: '/overview',
    element: <ComputeCenterOverview scope="data-center" />,
  },
];
const resourceOverview = [{ path: '/overview', element: <DataCenterOverview /> }];

// 态势感知
const situationAwareness = [
  {
    title: 'situation-awareness-cluster',
    path: '/situation-awareness/cluster',
    element: <SACluster />,
  },
  {
    title: 'situation-awareness-cluster-detail',
    path: '/situation-awareness/cluster/detail/:node',
    element: <SAClusterDetail />,
  },
  {
    title: 'situation-awareness-container',
    path: '/situation-awareness/container',
    element: <SAContainer />,
  },
  {
    title: 'situation-awareness-container-detail',
    path: '/situation-awareness/container/detail/:id/namespace/:namespace/pod/:pod/container/:container',
    element: <SAContainerDetail />,
  },
  {
    title: 'situation-awareness-gpu',
    path: '/situation-awareness/gpu',
    element: <SAGpu />,
  },
  {
    title: 'situation-awareness-gpu-detail',
    path: '/situation-awareness/gpu/gpuIndex/:gpuIndex/gpuModelName/:gpuModelName/gpuNode/:gpuNode/gpuVersion/:gpuVersion/exportedContainer/:exportedContainer/exportedPod/:exportedPod/exportedNamespace/:exportedNamespace',
    element: <SAGpuDetail />,
  },
  {
    title: 'situation-awareness-datasources',
    path: '/situation-awareness/datasources',
    element: <SAResource />,
  },
  {
    title: 'situation-awareness-datasources-add',
    path: '/situation-awareness/datasources/add',
    element: <SAResourceAdd />,
  },
  {
    title: 'situation-awareness-datasources-detail',
    path: '/situation-awareness/datasources/:id',
    element: <SAResourceDetail />,
  },
  {
    title: 'elec-report',
    path: '/report/elec-report',
    element: <ElectricReport />,
  },
];

// 产品管理：类别/产品列表已迁系统侧；此处仅保留 SKU
const productManage = [
  {
    title: 'productSku',
    path: '/products/skus',
    element: <ProductSku />,
  },
  {
    title: 'productSku',
    path: '/products/:productId/skus',
    element: <ProductSku />,
  },
  {
    title: 'productSkuDetail',
    path: '/products/skus/:skuId',
    element: <ProductSkuDetail />,
  },
  {
    title: 'productSkuDetail',
    path: '/products/:productId/skus/:skuId',
    element: <ProductSkuDetail />,
  },
  {
    title: 'productSkuCreate',
    path: '/products/skus/create',
    element: <ProductSkuCreate />,
  },
  {
    title: 'productSkuEdit',
    path: '/products/skus/:skuId/edit',
    element: <ProductSkuCreate />,
  },
  {
    title: 'productSkuEdit',
    path: '/products/:productId/skus/:skuId/edit',
    element: <ProductSkuCreate />,
  },
];
// 计费管理
const billManage = [
  {
    title: 'billItems',
    path: '/bill-items',
    element: <BillItems />,
  },
  {
    title: 'billItemsCreate',
    path: '/bill-items/create',
    element: <BillItemsCreate />,
  },
  {
    title: 'billItemsEdit',
    path: '/bill-items/:billItemId/edit',
    element: <BillItemsCreate />,
  },
  {
    title: 'billModelsBindItem',
    path: '/bill-models/:billModelId/bill-items/bind',
    element: <BindBillItemPage />,
  },
  {
    title: 'billItemsEdit',
    path: '/bill-models/:billModelId/bill-items/:billItemId/edit',
    element: <BillItemsCreate />,
  },
  {
    title: 'billItemsDetail',
    path: '/bill-items/:billItemId',
    element: <BillItemsDetail />,
  },
  {
    title: 'billItemsDetail',
    path: '/bill-models/:billModelId/bill-items/:billItemId',
    element: <BillItemsDetail />,
  },
  {
    title: 'billModels',
    path: '/bill-models',
    element: <BillModels />,
  },
  {
    title: 'billModelsCreate',
    path: '/bill-models/create',
    element: <BillModelsCreate />,
  },
  {
    title: 'billModelsEdit',
    path: '/bill-models/:billModelId/edit',
    element: <BillModelsCreate />,
  },
  {
    title: 'billModelsDetail',
    path: '/bill-models/:billModelId',
    element: <BillModelsDetail />,
  },
];
// 资源管理
const resourceManage = [
  {
    title: 'atomResources',
    path: '/atom-resources',
    element: <AtomResources />,
  },
  {
    title: 'deployTypes',
    path: '/deploy-types',
    element: <DeployTypes />,
  },
  {
    title: 'tags',
    path: '/tags',
    element: <Tags />,
  },
  {
    title: 'tagsCreate',
    path: '/tags/create',
    element: <TagCreatePage />,
  },
  {
    title: 'tagsEdit',
    path: '/tags/edit/:id',
    element: <TagCreatePage />,
  },
  {
    title: 'tagDetail',
    path: '/tags/:tagId',
    element: <TagDetail />,
  },
  {
    title: 'couponStrategy',
    path: '/coupon-strategy',
    element: <CouponStrategyPage />,
  },
  {
    title: 'couponStrategyCreate',
    path: '/coupon-strategy/create',
    element: <CouponStrategyCreatePage />,
  },
  {
    title: 'couponStrategyEdit',
    path: '/coupon-strategy/:id/edit',
    element: <CouponStrategyCreatePage />,
  },
  {
    title: 'couponStrategyDetail',
    path: '/coupon-strategy/:id',
    element: <CouponStrategyDetailPage />,
  },
];
// 运营中心
const operationCenter = [...productManage, ...billManage, ...resourceManage];

// 费用中心
const costCenter = [
  ...commonCostCenter,
  {
    title: 'chargeOverview',
    path: '/charges',
    element: <Charge />,
  },
];

// 安全中心
const securityCenter = [
  {
    title: 'platform-user',
    path: '/users',
    element: <Members />,
  },
  {
    title: 'platform-roles',
    path: '/roles',
    element: <Roles />,
  },
  ...auditLog,
];

// 计算资源
const computingResource = [
  {
    title: t('cluster:cluster-node-pl'),
    path: '/:type/nodes',
    element: <Node />,
  },
  {
    title: t('auth-conf:get.node.details'),
    path: '/:type/nodes/:node',
    element: <NodeDetails />,
  },
  {
    title: t('cluster:edit-label'),
    path: '/:type/nodes/:node/edit-label',
    element: <EditLabel />,
  },
];

// 存储类
const storageClass = [
  {
    title: 'storageClasses',
    path: '/storageclasses',
    element: <StorageClass />,
  },
  {
    title: 'create-storageclass',
    path: '/storageclasses/create',
    element: <CreateStorageClass />,
  },
  {
    title: 'storageclass-detail',
    path: '/storageclasses/:storageclass/detail',
    element: <StorageClassDetail />,
  },
];

// 网络设备
const networkDevice = [
  {
    path: '/network-device',
    element: <NetworkDevice />,
  },
  {
    path: '/network-device/:id/detail',
    element: <NetworkDeviceDetail />,
  },
  {
    path: '/network-device/create',
    element: <NetworkDeviceCreate />,
  },
  {
    path: '/network-device/:id/edit',
    element: <NetworkDeviceCreate />,
  },
];

// 资产管理
const assetManagement = [
  {
    title: 'gateway',
    path: '/bare-metal/gateway',
    element: <BareMetalGateway />,
  },
  {
    path: '/bare-metal/gateway/edit/:id',
    element: <BareMetalGatewayEdit />,
  },
  {
    path: '/bare-metal/gateway/add',
    element: <BareMetalGatewayEdit />,
  },
  {
    path: '/bare-metal/gateway/detail/:id',
    element: <BareMetalGatewayDetail />,
  },
  ...networkDevice,
  {
    title: 'node',
    path: '/bare-metal/node',
    element: <BareMetalNode />,
  },
  {
    path: '/bare-metal/node/detail/:id',
    element: <BareMetalNodeDetail />,
  },
  {
    path: '/bare-metal/node/add',
    element: <BareMetalNodeEdit />,
  },
  {
    path: '/bare-metal/node/:type/:id',
    element: <BareMetalNodeEdit />,
  },
  {
    title: 'bareMetalPool',
    path: '/bare-metal/node-groups',
    element: <BareMetalPool />,
  },
  {
    path: '/bare-metal/node-groups/create',
    element: <BareMetalNodeGroupCreate />,
  },
  {
    path: '/bare-metal/node-groups/edit/:id',
    element: <BareMetalNodeGroupCreate />,
  },
  {
    path: '/bare-metal/node-groups/detail/:id',
    element: <BareMetalPoolDetail />,
  },
  /** 节点组成员进入裸金属节点详情（面包屑：列表 → 节点组 → 节点） */
  {
    path: '/bare-metal/node-groups/detail/:groupId/node/:id',
    element: <BareMetalNodeDetail />,
  },
  ...clusterAsset,
  ...storageClass,
  // 硬件资产
  {
    path: '/hardware',
    element: <Hardware />,
  },
  {
    path: '/hardware/:type/detail',
    element: <HardwareDetail />,
  },
  {
    path: '/hardware/:type/node/:node/device/:device/detail',
    element: <HardwareInstanceDetail />,
  },
  {
    path: '/hardware/:type/edit',
    element: <HardwareEdit />,
  },
];

const pathPrefixes = ['/data-center/:cluster'];
const leafRoutes = mergeRouters([
  overview,
  artifactCenter,
  networkSecurity,
  intelligentStorage,
  instanceManagement,
  securityCenter,
  computingResource,
  assetManagement,
]);
const subCenterRoutes = [
  ...createSubCenterRoutes([resourceOverview, computingResource, assetManagement, securityCenter], SUB_CENTER.RESOURCE, ['data-center']),
  // 安全中心挂到各子中心下，保证 fixBottom 非外链跳转不丢失子中心上下文
  ...createSubCenterRoutes([operationOverview, situationAwareness, monitorCenter, logCenter, alertCenter, securityCenter], SUB_CENTER.OPS, [
    'data-center',
  ]),
  ...createSubCenterRoutes([aiCenterDc, securityCenter], SUB_CENTER.AI, ['data-center']),
  ...createSubCenterRoutes([appCenter, appCenterDcOnly, apiKeyManagement, securityCenter], SUB_CENTER.APP, ['data-center']),
  ...createSubCenterRoutes([operationCenter, costCenter, securityCenter], SUB_CENTER.BIZ, ['data-center']),
];
export const dataCenterRouters = {
  path: '/',
  element: <AppLayout />,
  title: t('common:platform.management'),
  children: [
    {
      element: <LeftNavLayout />,
      errorElement: <ErrorBoundary />,
      children: [...joinPrefix(leafRoutes, pathPrefixes), ...subCenterRoutes],
    },
  ],
};
